'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { type FocusEvent, type MouseEvent, useEffect, useId, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Alert01IconSolid, CheckmarkCircle02IconSolid } from '@/components/ui/icon/glyphs';
import type { OutcomeColorClasses } from '@/hooks/chart/use-outcome-color';
import { EASE_DEFAULT_MOTION, MOTION_ENTRANCE } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import type { AutoTradePending } from '@/stores/chart-settings';
const CHIP_MORPH = {
  type: 'spring' as const,
  visualDuration: 0.16,
  bounce: 0.16
};
const CHIP_REDUCED = {
  duration: 0
} as const;
const CHIP_FLIP = {
  duration: 0.18,
  ease: EASE_DEFAULT_MOTION
} as const;
const CHIP_COPY_DELAYED = {
  ...MOTION_ENTRANCE,
  delay: 0.12
} as const;
const COPY_INITIAL = {
  opacity: 0,
  y: 2
} as const;
const COPY_VISIBLE = {
  opacity: 1,
  y: 0
} as const;
const COPY_EXIT = {
  opacity: 0,
  y: -2
} as const;
const COPY_EXIT_REDUCED = {
  opacity: 0,
  y: 0,
  transition: CHIP_REDUCED
} as const;
export interface PositionQuickTradeChipId {
  direction: 'buy' | 'sell';
  buyUsdc?: number;
  sellPercent?: number;
  max?: boolean;
}
export interface PositionQuickTradeChip {
  kind: 'buy' | 'sell';
  label: string;
  onClick: () => void;
  id: PositionQuickTradeChipId;
  disabled?: boolean;
}
interface QuickTradeSlot {
  front: PositionQuickTradeChip;
  back: PositionQuickTradeChip;
  reveal: 'buy' | 'sell';
}
function quickTradeChipKey(id: PositionQuickTradeChipId): string {
  if (id.max === true) return 'buy:max';
  if (id.buyUsdc !== undefined) return `buy:${id.buyUsdc}`;
  if (id.sellPercent !== undefined) return `sell:${id.sellPercent}`;
  return id.direction;
}
export function PositionQuickTrade(t0) {
  const $ = _c(29);
  const {
    chips,
    color,
    progress,
    greenBuyChips: t1,
    className
  } = t0;
  const greenBuyChips = t1 === undefined ? false : t1;
  const t = useTranslations("market.position");
  const prefersReduced = useReducedMotion() === true;
  const [preview, setPreview] = useState(null);
  const layoutScope = useId();
  const rootRef = useRef(null);
  const pointerInsideRef = useRef(false);
  const hadProgressRef = useRef(false);
  const lastActivatedRef = useRef(null);
  const slots = createSlots(chips);
  let t2;
  if ($[0] !== progress) {
    t2 = progress === null ? null : quickTradeChipKey(progress);
    $[0] = progress;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const activeKey = t2;
  let t3;
  if ($[2] !== activeKey || $[3] !== chips) {
    t3 = activeKey === null ? undefined : chips.find(chip => quickTradeChipKey(chip.id) === activeKey);
    $[2] = activeKey;
    $[3] = chips;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const activeChip = t3;
  const activeSlotIndex = activeKey === null ? -1 : slots.findIndex(slot => quickTradeChipKey(slot.front.id) === activeKey || quickTradeChipKey(slot.back.id) === activeKey);
  const visibleDirection = progress?.direction ?? preview;
  const transition = prefersReduced ? CHIP_REDUCED : CHIP_MORPH;
  let t4;
  let t5;
  if ($[5] !== progress) {
    t4 = () => {
      if (progress !== null) {
        hadProgressRef.current = true;
        return;
      }
      if (!hadProgressRef.current) {
        return;
      }
      hadProgressRef.current = false;
      const lastActivated = lastActivatedRef.current;
      const faces = Array.from(rootRef.current?.querySelectorAll("[data-quick-trade-chip]") ?? []);
      const preferred = faces.find(face => face.dataset.quickTradeChip === lastActivated?.key && face.getAttribute("aria-hidden") !== "true");
      const fallback = faces.find(face_0 => face_0.dataset.quickTradeKind === lastActivated?.kind && face_0.getAttribute("aria-hidden") !== "true");
      const target = preferred ?? fallback;
      const frame = window.requestAnimationFrame(() => {
        if (lastActivated?.restoreFocus !== true && !pointerInsideRef.current) {
          return;
        }
        target?.querySelector("button")?.focus();
      });
      return () => window.cancelAnimationFrame(frame);
    };
    t5 = [progress];
    $[5] = progress;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  if (slots.length === 0) {
    return null;
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function activate(chip_0, restoreFocus) {
      setPreview(chip_0.kind);
      lastActivatedRef.current = {
        key: quickTradeChipKey(chip_0.id),
        kind: chip_0.kind,
        restoreFocus
      };
      chip_0.onClick();
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const activate = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function handleBlur(event) {
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
        setPreview(null);
      }
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleBlur = t7;
  const T0 = LazyMotion;
  const t8 = domMax;
  let t9;
  if ($[10] !== t) {
    t9 = t("quickTradeLabel");
    $[10] = t;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = "position-quick-trade";
  const t11 = visibleDirection ?? "default";
  const t12 = progress?.phase ?? "idle";
  let t13;
  if ($[12] !== className) {
    t13 = cn("relative grid h-9 min-w-0 grid-cols-4 gap-2.5 border-0 p-0 [perspective:32.5rem]", className);
    $[12] = className;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  let t15;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      pointerInsideRef.current = true;
    };
    t15 = () => {
      pointerInsideRef.current = false;
      setPreview(null);
    };
    $[14] = t14;
    $[15] = t15;
  } else {
    t14 = $[14];
    t15 = $[15];
  }
  const t16 = progress !== null && activeChip !== undefined && activeSlotIndex >= 0 ? <QuickTradeFlight layoutId={`${layoutScope}-slot-${activeSlotIndex}`} chip={activeChip} color={color} progress={progress} greenBuyChips={greenBuyChips} prefersReduced={prefersReduced} transition={transition} /> : slots.map((slot_0, slotIndex) => {
    const flipped = visibleDirection === slot_0.reveal;
    return <QuickTradeFlipSlot key={`${quickTradeChipKey(slot_0.front.id)}-${quickTradeChipKey(slot_0.back.id)}`} layoutId={`${layoutScope}-slot-${slotIndex}`} slotIndex={slotIndex} slot={slot_0} flipped={flipped} color={color} greenBuyChips={greenBuyChips} prefersReduced={prefersReduced} transition={transition} onPreview={setPreview} onActivate={activate} />;
  });
  let t17;
  if ($[16] !== handleBlur || $[17] !== t11 || $[18] !== t12 || $[19] !== t13 || $[20] !== t14 || $[21] !== t15 || $[22] !== t16 || $[23] !== t9) {
    t17 = <fieldset ref={rootRef} aria-label={t9} data-testid={t10} data-direction={t11} data-phase={t12} className={t13} onPointerEnter={t14} onPointerLeave={t15} onBlur={handleBlur}>{t16}</fieldset>;
    $[16] = handleBlur;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
    $[23] = t9;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== T0 || $[26] !== t17 || $[27] !== t8) {
    t18 = <T0 features={t8}>{t17}</T0>;
    $[25] = T0;
    $[26] = t17;
    $[27] = t8;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  return t18;
}
function createSlots(chips: readonly PositionQuickTradeChip[]): readonly QuickTradeSlot[] {
  const buys = chips.filter(chip => chip.kind === 'buy');
  const sells = chips.filter(chip => chip.kind === 'sell');
  const [buySmall, buyMedium, buyLarge, buyXLarge] = buys;
  const [sellQuarter, sellHalf, sellThreeQuarters, sellAll] = sells;
  if (buySmall === undefined || buyMedium === undefined || buyLarge === undefined || buyXLarge === undefined || sellQuarter === undefined || sellHalf === undefined || sellThreeQuarters === undefined || sellAll === undefined) {
    return [];
  }
  return [{
    front: buySmall,
    back: sellQuarter,
    reveal: 'sell'
  }, {
    front: buyMedium,
    back: sellHalf,
    reveal: 'sell'
  }, {
    front: sellThreeQuarters,
    back: buyLarge,
    reveal: 'buy'
  }, {
    front: sellAll,
    back: buyXLarge,
    reveal: 'buy'
  }];
}
function QuickTradeFlipSlot(t0) {
  const $ = _c(26);
  const {
    layoutId,
    slotIndex,
    slot,
    flipped,
    color,
    greenBuyChips,
    prefersReduced,
    transition,
    onPreview,
    onActivate
  } = t0;
  const t1 = flipped ? -180 : 0;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      rotateX: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = prefersReduced ? CHIP_REDUCED : CHIP_FLIP;
  let t4;
  if ($[2] !== color || $[3] !== flipped || $[4] !== greenBuyChips || $[5] !== onActivate || $[6] !== onPreview || $[7] !== slot.front) {
    t4 = <QuickTradeFace chip={slot.front} hidden={flipped} side="front" color={color} greenBuyChips={greenBuyChips} onPreview={onPreview} onActivate={onActivate} />;
    $[2] = color;
    $[3] = flipped;
    $[4] = greenBuyChips;
    $[5] = onActivate;
    $[6] = onPreview;
    $[7] = slot.front;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !flipped;
  let t6;
  if ($[9] !== color || $[10] !== greenBuyChips || $[11] !== onActivate || $[12] !== onPreview || $[13] !== slot.back || $[14] !== t5) {
    t6 = <QuickTradeFace chip={slot.back} hidden={t5} side="back" color={color} greenBuyChips={greenBuyChips} onPreview={onPreview} onActivate={onActivate} />;
    $[9] = color;
    $[10] = greenBuyChips;
    $[11] = onActivate;
    $[12] = onPreview;
    $[13] = slot.back;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t6) {
    t7 = <m.div initial={false} animate={t2} transition={t3} className="absolute inset-0 [transform-style:preserve-3d]">{t4}{t6}</m.div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== layoutId || $[22] !== slotIndex || $[23] !== t7 || $[24] !== transition) {
    t8 = <m.div layoutId={layoutId} transition={transition} data-quick-trade-slot={slotIndex} className="relative h-9 min-w-0">{t7}</m.div>;
    $[21] = layoutId;
    $[22] = slotIndex;
    $[23] = t7;
    $[24] = transition;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
function QuickTradeFace(t0) {
  const $ = _c(25);
  const {
    chip,
    hidden,
    side,
    color,
    greenBuyChips,
    onPreview,
    onActivate
  } = t0;
  let t1;
  if ($[0] !== chip.id) {
    t1 = quickTradeChipKey(chip.id);
    $[0] = chip.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = chip.kind;
  const t3 = side === "back" && "[transform:rotateX(180deg)]";
  const t4 = hidden ? "pointer-events-none" : "pointer-events-auto";
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = cn("absolute inset-0 [backface-visibility:hidden] [-webkit-backface-visibility:hidden]", t3, t4);
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  let t7;
  if ($[5] !== chip.kind || $[6] !== onPreview) {
    t6 = () => onPreview(chip.kind);
    t7 = () => onPreview(chip.kind);
    $[5] = chip.kind;
    $[6] = onPreview;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== chip || $[10] !== onActivate) {
    t8 = event => onActivate(chip, event.detail === 0);
    $[9] = chip;
    $[10] = onActivate;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== chip || $[13] !== color || $[14] !== greenBuyChips || $[15] !== t8) {
    t9 = <QuickTradeButton chip={chip} color={color} greenBuyChips={greenBuyChips} onClick={t8} />;
    $[12] = chip;
    $[13] = color;
    $[14] = greenBuyChips;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== chip.kind || $[18] !== hidden || $[19] !== t1 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t9) {
    t10 = <div inert={hidden} aria-hidden={hidden} data-quick-trade-chip={t1} data-quick-trade-kind={t2} className={t5} onPointerEnter={t6} onFocusCapture={t7}>{t9}</div>;
    $[17] = chip.kind;
    $[18] = hidden;
    $[19] = t1;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function QuickTradeFlight(t0) {
  const $ = _c(27);
  const {
    layoutId,
    chip,
    color,
    progress,
    greenBuyChips,
    prefersReduced,
    transition
  } = t0;
  const t = useTranslations("market.position");
  const phase = progress.phase ?? "pending";
  let copy;
  if ($[0] !== chip.kind || $[1] !== chip.label || $[2] !== phase || $[3] !== t) {
    const amount = chip.label.replace(/^[+-]/, "");
    let t1;
    if ($[5] !== t) {
      t1 = t("quickTradeFailed");
      $[5] = t;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    copy = t1;
    if (phase === "pending") {
      copy = chip.kind === "buy" ? t("quickTradeBuying", {
        amount
      }) : t("quickTradeSelling", {
        amount
      });
    } else {
      if (phase === "success") {
        copy = chip.kind === "buy" ? t("quickTradeBought", {
          amount
        }) : t("quickTradeSold", {
          amount
        });
      }
    }
    $[0] = chip.kind;
    $[1] = chip.label;
    $[2] = phase;
    $[3] = t;
    $[4] = copy;
  } else {
    copy = $[4];
  }
  const prefix = phase === "success" ? CheckmarkCircle02IconSolid : phase === "error" ? Alert01IconSolid : undefined;
  const t1 = phase === "pending";
  let t2;
  if ($[7] !== chip.kind || $[8] !== color || $[9] !== greenBuyChips || $[10] !== phase) {
    t2 = cn("w-full rounded-pill text-caption font-medium tabular-nums disabled:opacity-100", tradeChipTone(chip.kind, color, greenBuyChips, phase === "error"));
    $[7] = chip.kind;
    $[8] = color;
    $[9] = greenBuyChips;
    $[10] = phase;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = prefersReduced ? false : COPY_INITIAL;
  const t4 = prefersReduced ? COPY_EXIT_REDUCED : COPY_EXIT;
  const t5 = prefersReduced ? CHIP_REDUCED : phase === "pending" ? CHIP_COPY_DELAYED : MOTION_ENTRANCE;
  let t6;
  if ($[12] !== copy || $[13] !== phase || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <AnimatePresence initial={false} mode="popLayout"><m.span key={phase} initial={t3} animate={COPY_VISIBLE} exit={t4} transition={t5}>{copy}</m.span></AnimatePresence>;
    $[12] = copy;
    $[13] = phase;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== prefix || $[19] !== t1 || $[20] !== t2 || $[21] !== t6) {
    t7 = <AnimatedButton variant="tertiary" disabled={true} loading={t1} prefix={prefix} disableHoverScale={true} disableTapScale={true} className={t2}>{t6}</AnimatedButton>;
    $[18] = prefix;
    $[19] = t1;
    $[20] = t2;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== layoutId || $[24] !== t7 || $[25] !== transition) {
    t8 = <m.div layoutId={layoutId} layout={true} role="status" aria-live="polite" transition={transition} className="col-span-4 col-start-1 row-start-1 min-w-0">{t7}</m.div>;
    $[23] = layoutId;
    $[24] = t7;
    $[25] = transition;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
function QuickTradeButton(t0) {
  const $ = _c(9);
  const {
    chip,
    color,
    greenBuyChips,
    onClick
  } = t0;
  const t1 = chip.disabled === true ? undefined : onClick;
  const t2 = chip.disabled === true;
  let t3;
  if ($[0] !== chip.kind || $[1] !== color || $[2] !== greenBuyChips) {
    t3 = cn("w-full rounded-pill text-caption font-medium tabular-nums", tradeChipTone(chip.kind, color, greenBuyChips, false));
    $[0] = chip.kind;
    $[1] = color;
    $[2] = greenBuyChips;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== chip.label || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = <AnimatedButton variant="tertiary" onClick={t1} disabled={t2} disableHoverScale={true} className={t3}>{chip.label}</AnimatedButton>;
    $[4] = chip.label;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function tradeChipTone(kind: 'buy' | 'sell', color: Pick<OutcomeColorClasses, 'hoverFill' | 'outline' | 'text'>, greenBuyChips: boolean, failed: boolean): string {
  if (failed || kind === 'sell') {
    return 'border bg-feedback-error-surface text-feedback-error-content hover:bg-feedback-error-surface/50';
  }
  if (greenBuyChips) {
    return 'border-feedback-success-border bg-feedback-success-surface text-feedback-success-content hover:bg-feedback-success-surface/50';
  }
  return cn(color.outline, color.hoverFill, color.text);
}